/*
 * The segmented toggle: "View by: Fencer / Tournament" in Upcoming and Past.
 *
 * THE COLOUR RULE, which is the interesting part and is easy to get backwards
 * (§10, verbatim):
 *
 *   "the unselected (actionable) segment is red text; the selected segment is a
 *   neutral fill — red marks the only tappable half."
 *
 * That inverts the usual convention, and it follows from the one-directional
 * accent rule rather than from taste: red means "you can touch this", so the
 * segment you are ALREADY on must not be red, because pressing it does nothing.
 * A conventional toggle with the active segment highlighted in the accent colour
 * would be claiming an interaction that is not there.
 *
 * Implemented as a radiogroup rather than as two buttons, because that is what
 * it is: a single-choice control where exactly one option holds. A screen reader
 * then announces "2 of 2" and the arrow keys work without us implementing them.
 */

import { useId } from "react";
import { useTranslation } from "react-i18next";

import styles from "./SegmentedToggle.module.css";

export type GroupBy = "tournament" | "fencer";

export interface SegmentedToggleProps {
  value: GroupBy;
  onChange: (next: GroupBy) => void;

  /**
   * The group's accessible label. Defaults to "View by", which is the visible
   * text §10 specifies; passing a different one is for a second toggle on a
   * screen that ever has two.
   */
  label?: string;
}

const OPTIONS: GroupBy[] = ["tournament", "fencer"];

export function SegmentedToggle({ value, onChange, label }: SegmentedToggleProps) {
  const { t } = useTranslation();
  const groupLabelId = useId();

  return (
    <div className={styles.wrap}>
      <span id={groupLabelId} className={styles.label}>
        {label ?? t("toggle.viewBy")}
      </span>

      <div className={styles.group} role="radiogroup" aria-labelledby={groupLabelId}>
        {OPTIONS.map((option) => {
          const selected = option === value;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              // Only the actionable segment is reachable by Tab, which is what a
              // radiogroup does: Tab enters the group at the current value and
              // arrows move within it. Here the selected one holds the tab stop
              // so focus lands somewhere meaningful.
              tabIndex={selected ? 0 : -1}
              className={selected ? styles.selected : styles.unselected}
              onClick={() => onChange(option)}
              onKeyDown={(e) => {
                // Arrow keys move between segments, per the radiogroup pattern.
                if (e.key === "ArrowRight" || e.key === "ArrowLeft" ||
                    e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                  const other = OPTIONS.find((o) => o !== value);
                  if (other) onChange(other);
                }
              }}
            >
              {t(`toggle.${option}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
